'use client';

import * as React from 'react';
import { fileStorageService } from '@/lib/supabase/file-storage-service';

export interface SignedPhotoStripPhoto {
  id: string;
  fileName: string;
  filePath: string;
}

interface SignedPhotoStripProps {
  photos: SignedPhotoStripPhoto[];
  /** Called with a freshly signed URL when a thumbnail is clicked. */
  onOpen: (photo: SignedPhotoStripPhoto, url: string) => void;
  /** Called when no URL could be signed for a clicked thumbnail. */
  onOpenError?: (photo: SignedPhotoStripPhoto) => void;
}

/**
 * Row of photo thumbnails backed by short-lived signed URLs.
 *
 * The pin-files bucket is private, so a thumbnail cannot be built from a
 * synchronous public URL. All thumbnails are signed in one batched request.
 * A thumbnail whose URL has expired (the panel was left open for over an
 * hour) is re-signed once on its load error, and a click always re-requests
 * the URL, so the viewer never receives a stale one.
 */
export function SignedPhotoStrip({ photos, onOpen, onOpenError }: SignedPhotoStripProps) {
  const [urls, setUrls] = React.useState<Record<string, string>>({});
  const [loaded, setLoaded] = React.useState(false);
  const refreshedPaths = React.useRef(new Set<string>());

  // Re-sign only when the set of files changes, not on every parent render
  const pathsKey = photos.map(p => p.filePath).join('\n');

  React.useEffect(() => {
    let active = true;
    setLoaded(false);
    refreshedPaths.current.clear();

    fileStorageService.getSignedUrls(pathsKey ? pathsKey.split('\n') : []).then(result => {
      if (!active) return;
      setUrls(result);
      setLoaded(true);
    });

    return () => {
      active = false;
    };
  }, [pathsKey]);

  const handleImageError = async (filePath: string) => {
    if (refreshedPaths.current.has(filePath)) return;
    refreshedPaths.current.add(filePath);
    const url = await fileStorageService.getSignedUrl(filePath, { forceRefresh: true });
    if (url) setUrls(prev => ({ ...prev, [filePath]: url }));
  };

  const handleClick = async (photo: SignedPhotoStripPhoto) => {
    const url = await fileStorageService.getSignedUrl(photo.filePath);
    if (url) {
      onOpen(photo, url);
    } else {
      onOpenError?.(photo);
    }
  };

  return (
    <>
      {photos.map(photo => {
        const url = urls[photo.filePath];

        if (!url) {
          // Placeholder while signing; nothing once signing has failed
          return loaded ? null : (
            <div
              key={photo.id}
              className="flex-shrink-0 w-14 h-14 rounded border border-border bg-muted animate-pulse"
              aria-hidden="true"
            />
          );
        }

        return (
          <button
            key={photo.id}
            onClick={() => handleClick(photo)}
            className="flex-shrink-0 rounded overflow-hidden border border-border hover:border-primary transition-colors"
            title={photo.fileName}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={url}
              alt={photo.fileName}
              className="w-14 h-14 object-cover"
              onError={() => handleImageError(photo.filePath)}
            />
          </button>
        );
      })}
    </>
  );
}
